import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { erApiFeil } from '../api/feil.ts';
import { useKilder } from '../api/useHypoteseKilder.ts';
import { HENDELSER, useWebhooks } from '../api/useWebhooks.ts';
import { useHypoteseConfig } from '../api/useHypotheses.ts';
import { useInnstillinger, useOppdaterInnstillinger, type TenantSettings, type TenantSettingsUpdate } from '../api/useSettings.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { Kartknapp } from '../innboks/Kartknapp.tsx';
import type { Tast } from '../innboks/Tastaturkart.tsx';
import { Toast, type ToastInnhold } from '../innboks/Toast.tsx';
import { Bryter } from '../kjentefeil/Bryter.tsx';
import { Fot } from '../innstillinger/Fot.tsx';
import { Rad, RadProvider, Seksjon, TekniskeBryter, useRadstyring } from '../innstillinger/Rad.tsx';
import { useUtkast, type Utkast } from '../innstillinger/utkast.tsx';
import { Om } from '../skall/Om.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { hypoteseOppforing } from '../status/status.ts';

/**
 * Innstillinger for rommet Hypoteser (H2.3; konseptets skjerm P9, kravlisten `admin-hypoteser.jsx`
 * SideInnstillinger) — eierens side. Én rad per innstilling «navn · verdi i én linje · Endre», fem
 * grupper, og ordlyd, koder, maler og beslutningstakere som egne undersider bak «Endre». Hele
 * flaten — siden og undersidene — er ÉTT utkast (`hypoteser`, P3) med foten som eneste
 * primærvalg, og ÉN `PATCH /v1/admin/settings` med bare feltene som er endret.
 *
 * Verdilinjen er lukket: takene og regelen om korting står bak «Om fristene», aldri i linjen
 * (avvik 21 i `hypotesemodul-avvik-v0.3`). En verdi som settes tilbake til den lagrede, er ingen
 * endring — feltet tas ut av utkastet, og «Lagre» er sperret igjen.
 *
 * Ingen innstilling skrur rommet på — det gjør bare plattformen (`tenant.rooms`).
 */

type HUtkast = Pick<
  TenantSettingsUpdate,
  | 'hypothesisReviewDays'
  | 'hypothesisConclusionDays'
  | 'hypothesisSuggestedTestDays'
  | 'hypothesisMinFieldLength'
  | 'hypothesisDeciders'
  | 'hypothesisReviewerMayReject'
  | 'hypothesisStatusWording'
  | 'hypothesisRejectReasonWording'
  | 'hypothesisStopReasonWording'
  | 'hypothesisResponseTemplates'
  | 'hypothesisHelpText'
  | 'hypothesisRetentionYears'
  | 'hypothesisScoresEnabled'
>;
type Felt = keyof HUtkast;
type Mal = NonNullable<HUtkast['hypothesisResponseTemplates']>[number];
type Bruk = Mal['use'];

/** Undersidene bak «Endre» (`admin-hypoteser.jsx` UNDERSIDER): adressen er nøkkelen. */
export const UNDERSIDER = {
  statusnavn: 'Statusnavn og løfter',
  avslagskoder: 'Avslagskoder',
  stoppkoder: 'Stoppkoder',
  svarmaler: 'Svarmaler',
  beslutningstakere: 'Beslutningstakere',
} as const;
export type Underside = keyof typeof UNDERSIDER;

/** Sidens ene setning under overskriften — wireframens, ordrett. */
const UNDERSIDE_TEKST: Record<Underside, string> = {
  statusnavn: 'Navnet står på pillen, løftet under den — slik innsenderen ser det. Ikonet og regelen er plattformens.',
  avslagskoder: 'Koden er fast, så registeret kan sammenlignes over tid. Dere eier etiketten og malen, og kan skjule en kode.',
  stoppkoder: 'Koden er fast. Dere eier etiketten og malen.',
  svarmaler: 'Malen er utgangspunktet, aldri hele svaret.',
  beslutningstakere: 'De som velger, parkerer og avviser. En beslutningstaker avgjør aldri sin egen hypotese.',
};

/**
 * Hvilken seksjon eier hvilket utkastfelt PÅ DENNE SIDEN. Vakt 9 i `test/lint-vakter.test.ts`
 * leser kartet og skrivekallene ut av denne filen og krever at hvert felt står i nøyaktig én
 * seksjon på nøyaktig én innstillingsside.
 */
export const FELT_I_SEKSJON: Readonly<Record<string, readonly string[]>> = {
  hvem: ['hypothesisDeciders', 'hypothesisReviewerMayReject'],
  frister: ['hypothesisReviewDays', 'hypothesisConclusionDays', 'hypothesisSuggestedTestDays'],
  skjemaet: ['hypothesisMinFieldLength', 'hypothesisHelpText'],
  ordlyd: ['hypothesisStatusWording', 'hypothesisRejectReasonWording', 'hypothesisStopReasonWording', 'hypothesisResponseTemplates'],
  oppbevaring: ['hypothesisRetentionYears'],
  moeter: ['hypothesisScoresEnabled'],
};

/** Radenes navn, for «endret»-merket og fotens «hva lagres» — aldri kontraktens feltnavn (DD-50). */
const FELT_I_RAD: ReadonlyArray<{ rad: string; navn: string; felt: Felt }> = [
  { rad: 'beslutningstakere', navn: 'Beslutningstakere', felt: 'hypothesisDeciders' },
  { rad: 'kan-avvise', navn: 'Vurdereren kan avvise', felt: 'hypothesisReviewerMayReject' },
  { rad: 'avgjorelse', navn: 'Avgjørelse', felt: 'hypothesisReviewDays' },
  { rad: 'konklusjon', navn: 'Konklusjon', felt: 'hypothesisConclusionDays' },
  { rad: 'testperiode', navn: 'Foreslått testperiode', felt: 'hypothesisSuggestedTestDays' },
  { rad: 'minste', navn: 'Minste antall tegn', felt: 'hypothesisMinFieldLength' },
  { rad: 'hjelpetekst', navn: 'Hjelpetekst', felt: 'hypothesisHelpText' },
  { rad: 'statusnavn', navn: 'Statusnavn og løfter', felt: 'hypothesisStatusWording' },
  { rad: 'avslagskoder', navn: 'Avslagskoder', felt: 'hypothesisRejectReasonWording' },
  { rad: 'stoppkoder', navn: 'Stoppkoder', felt: 'hypothesisStopReasonWording' },
  { rad: 'svarmaler', navn: 'Svarmaler', felt: 'hypothesisResponseTemplates' },
  { rad: 'oppbevaring', navn: 'Avsluttede hypoteser', felt: 'hypothesisRetentionYears' },
  { rad: 'tall', navn: 'Menneskesatte tall', felt: 'hypothesisScoresEnabled' },
];

/** Avgjørelsene en svarmal kan være for (`HypothesisTemplateUse`), i livsløpets rekkefølge. */
const BRUK: ReadonlyArray<[Bruk, string]> = [
  ['trenger_mer', 'Trenger mer'],
  ['avvist', 'Avvist'],
  ['parkert', 'Parkert'],
  ['valgt', 'Valgt'],
  ['justering', 'Justering'],
  ['pa_pause', 'På pause'],
  ['konkludert', 'Konkludert'],
  ['stoppet', 'Stoppet'],
];
const bruksnavn = (b: Bruk) => BRUK.find(([k]) => k === b)?.[1] ?? b;

/** Plattformens tak (kontrakten, `TenantSettingsUpdate`): leietakeren beveger seg under. */
const TAK = { frist: 90, testperiode: 365, minste: 100, aar: 10 } as const;
/** Kontraktens lengder: malsetning og svarmal (500), løftet (200), hjelpeteksten (2000). */
const MAKS = { mal: 500, lofte: 200, hjelp: 2000 } as const;

const TASTER: Tast[] = [['Ctrl+S', 'lagre endringer']];

const heltall = (v: number | undefined, min: number, maks: number) => v === undefined || (Number.isInteger(v) && v >= min && v <= maks);

/** Grunnen til at utkastet ikke kan lagres som det står — eller `null`. Første brudd vinner. */
function sperreFor(u: HUtkast): string | null {
  if (!heltall(u.hypothesisReviewDays, 1, TAK.frist)) return (u.hypothesisReviewDays ?? 0) > TAK.frist ? `Avgjørelse kan være høyst ${TAK.frist} dager.` : 'Avgjørelse må være minst 1 dag.';
  if (!heltall(u.hypothesisConclusionDays, 1, TAK.frist)) return (u.hypothesisConclusionDays ?? 0) > TAK.frist ? `Konklusjon kan være høyst ${TAK.frist} dager.` : 'Konklusjon må være minst 1 dag.';
  if (!heltall(u.hypothesisSuggestedTestDays, 1, TAK.testperiode)) return `Foreslått testperiode må være 1–${TAK.testperiode} dager.`;
  if (!heltall(u.hypothesisMinFieldLength, 1, TAK.minste)) return `Minste antall tegn må være 1–${TAK.minste}.`;
  if (!heltall(u.hypothesisRetentionYears, 1, TAK.aar)) return `Avsluttede hypoteser beholdes 1–${TAK.aar} år.`;
  if (u.hypothesisStatusWording?.some((w) => !w.label.trim())) return 'Hver status trenger et navn.';
  if (u.hypothesisStatusWording?.some((w) => !w.promise.trim())) return 'Hver status trenger et løfte.';
  if (u.hypothesisRejectReasonWording?.some((w) => !w.label.trim()) || u.hypothesisStopReasonWording?.some((w) => !w.label.trim())) return 'Hver kode trenger en etikett.';
  if (u.hypothesisResponseTemplates?.some((m) => !m.name.trim() || !m.text.trim())) return 'Hver mal trenger navn og tekst.';
  return null;
}

/** Det lagrede, i utkastets form — svarmalene uten id, hjelpeteksten tom når den mangler. */
function lagret(s: TenantSettings): Required<HUtkast> {
  return {
    hypothesisReviewDays: s.hypothesisReviewDays,
    hypothesisConclusionDays: s.hypothesisConclusionDays,
    hypothesisSuggestedTestDays: s.hypothesisSuggestedTestDays,
    hypothesisMinFieldLength: s.hypothesisMinFieldLength,
    hypothesisDeciders: s.hypothesisDeciders,
    hypothesisReviewerMayReject: s.hypothesisReviewerMayReject,
    hypothesisStatusWording: s.hypothesisStatusWording,
    hypothesisRejectReasonWording: s.hypothesisRejectReasonWording,
    hypothesisStopReasonWording: s.hypothesisStopReasonWording,
    hypothesisResponseTemplates: s.hypothesisResponseTemplates.map((m) => ({ name: m.name, text: m.text, use: m.use })),
    hypothesisHelpText: s.hypothesisHelpText ?? '',
    hypothesisRetentionYears: s.hypothesisRetentionYears,
    hypothesisScoresEnabled: s.hypothesisScoresEnabled,
  };
}

export function HInnstillinger({ underside }: { underside: Underside | null }) {
  const innstillinger = useInnstillinger();
  const config = useHypoteseConfig();
  const kilder = useKilder();
  // H3.5 (P7): hypotesehendelsene velges per mottaker i Organisasjon; her står bare hva som sendes.
  const mottakere = useWebhooks();
  const oppdater = useOppdaterInnstillinger();
  const { utkast: alt, sett: settUtkast, fjern, tøm, antall } = useUtkast('hypoteser');
  const utkast = alt as HUtkast;
  const [toast, setToast] = useState<ToastInnhold | null>(null);
  const [feil, setFeil] = useState<string | null>(null);
  // Siden finnes bare for eier (HModus); alle rader kan endres.
  const side = useRadstyring(true);

  const s = innstillinger.data;
  const fra = useMemo(() => (s ? lagret(s) : null), [s]);
  const verdi = useCallback(<K extends Felt>(felt: K): Required<HUtkast>[K] => (utkast[felt] !== undefined ? (utkast[felt] as Required<HUtkast>[K]) : fra![felt]), [utkast, fra]);

  /** Skriver til utkastet — og tar feltet ut igjen når verdien er den lagrede (uendret er ingen endring). */
  const sett = useCallback(
    <K extends Felt>(felt: K, v: Required<HUtkast>[K]) => {
      if (fra && JSON.stringify(v) === JSON.stringify(fra[felt])) fjern(felt as keyof Utkast);
      else settUtkast(felt as keyof Utkast, v as Utkast[keyof Utkast]);
    },
    [fra, fjern, settUtkast],
  );

  const endret = useCallback((rad: string) => FELT_I_RAD.some((r) => r.rad === rad && utkast[r.felt] !== undefined), [utkast]);
  const endretNavn = useMemo(() => FELT_I_RAD.filter((r) => utkast[r.felt] !== undefined).map((r) => r.navn), [utkast]);
  const sperret = useMemo(() => sperreFor(utkast), [utkast]);

  const lagre = useCallback(() => {
    if (oppdater.isPending || antall === 0 || sperret) return;
    setFeil(null);
    oppdater
      .mutateAsync(utkast as Utkast)
      .then(() => {
        tøm();
        setToast({ tekst: `Innstillingene for rommet er lagret — ${antall} ${antall === 1 ? 'endring' : 'endringer'}.` });
      })
      .catch((e: unknown) => setFeil(erApiFeil(e) ? e.brukertekst : 'Kunne ikke lagre. Prøv igjen.'));
  }, [oppdater, antall, sperret, utkast, tøm]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        lagre();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lagre]);

  // Navnene på rommets folk, for beslutningstakerne (ved id i TenantSettings).
  const folk = useMemo(() => {
    const kart = new Map<string, string>();
    for (const p of [...(config.data?.members ?? []), ...(config.data?.deciders ?? [])]) if (p.displayName) kart.set(p.id, p.displayName);
    return kart;
  }, [config.data]);
  const navnPaa = useCallback((id: string) => folk.get(id) ?? 'en kollega uten visningsnavn', [folk]);

  const tittel = underside ? UNDERSIDER[underside] : 'Innstillinger';

  if (innstillinger.isPending) {
    return (
      <>
        <header className="panel-topp">
          <h1 className="panel-h1">{tittel}</h1>
        </header>
        <div role="status" aria-busy="true" aria-label="Laster innstillingene" className="panel-skjelett">
          <span className="panel-skjelett-linje panel-skjelett-tittel" />
          <span className="panel-skjelett-linje" />
          <span className="panel-skjelett-linje panel-skjelett-kort" />
        </div>
      </>
    );
  }

  if (innstillinger.isError || !s || !fra) {
    return (
      <>
        <header className="panel-topp">
          <h1 className="panel-h1">{tittel}</h1>
        </header>
        <div role="alert" className="panel-melding panel-melding-feil">
          <Ikon navn="warning-outline" /> Kunne ikke laste innstillingene. Ingenting er endret.
          <button type="button" className="panel-knapp-liten" onClick={() => void innstillinger.refetch()}>
            Prøv igjen
          </button>
        </div>
      </>
    );
  }

  const deciders = verdi('hypothesisDeciders');
  const statuser = verdi('hypothesisStatusWording');
  const avslag = verdi('hypothesisRejectReasonWording');
  const stopp = verdi('hypothesisStopReasonWording');
  const maler = verdi('hypothesisResponseTemplates');
  const hjelp = verdi('hypothesisHelpText');
  const mottatt = statuser.find((w) => w.key === 'mottatt');
  const koder = (liste: Array<{ label: string; hidden: boolean }>) => liste.map((w) => (w.hidden ? `${w.label} (skjult)` : w.label)).join(' · ');
  const bruk = [...new Set(maler.map((m) => m.use))].map(bruksnavn);

  const tall = (id: string, etikett: string, felt: 'hypothesisReviewDays' | 'hypothesisConclusionDays' | 'hypothesisSuggestedTestDays' | 'hypothesisMinFieldLength' | 'hypothesisRetentionYears', maks: number, onEndre: (n: number) => void): ReactNode => (
    <label className="panel-felt h-innst-tall">
      <span>{etikett}</span>
      <input type="number" min={1} max={maks} value={Number.isNaN(verdi(felt)) ? '' : verdi(felt)} onChange={(e) => onEndre(e.target.value === '' ? Number.NaN : Number(e.target.value))} data-testid={`h-${id}`} />
    </label>
  );

  const hovedside = (
    <>
      <Seksjon id="hvem" tittel="Hvem">
        <Rad
          id="beslutningstakere"
          navn="Beslutningstakere"
          kode="hypothesisDeciders"
          handling="Endre"
          til="/h/innstillinger/beslutningstakere"
          endret={endret('beslutningstakere')}
          verdi={deciders.length ? <strong>{deciders.map(navnPaa).join(' · ')}</strong> : 'Ingen ennå'}
        />
        <Rad
          id="kan-avvise"
          navn="Vurdereren kan avvise"
          kode="hypothesisReviewerMayReject"
          handling={null}
          endret={endret('kan-avvise')}
          verdi={<Bryter label="Vurdereren kan avvise" skjultEtikett paa={verdi('hypothesisReviewerMayReject')} onChange={(v) => sett('hypothesisReviewerMayReject', v)} testid="kan-avvise-bryter" />}
        />
      </Seksjon>

      <Seksjon id="frister" tittel="Frister">
        <Rad
          id="avgjorelse"
          navn="Avgjørelse"
          kode="hypothesisReviewDays"
          handling="Endre"
          endret={endret('avgjorelse')}
          verdi={
            <>
              <strong>{verdi('hypothesisReviewDays')} dager</strong> fra hypotesen tas til vurdering
            </>
          }
          verdiSmal={`${verdi('hypothesisReviewDays')} dager`}
        >
          {tall('avgjorelse', 'Dager fra hypotesen tas til vurdering', 'hypothesisReviewDays', TAK.frist, (n) => sett('hypothesisReviewDays', n))}
        </Rad>
        <Rad
          id="konklusjon"
          navn="Konklusjon"
          kode="hypothesisConclusionDays"
          handling="Endre"
          endret={endret('konklusjon')}
          verdi={
            <>
              <strong>{verdi('hypothesisConclusionDays')} dager</strong> fra testens slutt
            </>
          }
          verdiSmal={`${verdi('hypothesisConclusionDays')} dager`}
        >
          {tall('konklusjon', 'Dager fra testens slutt', 'hypothesisConclusionDays', TAK.frist, (n) => sett('hypothesisConclusionDays', n))}
        </Rad>
        <Rad id="testperiode" navn="Foreslått testperiode" kode="hypothesisSuggestedTestDays" handling="Endre" endret={endret('testperiode')} verdi={<strong>{verdi('hypothesisSuggestedTestDays')} dager</strong>}>
          {tall('testperiode', 'Dager — et forslag i skjemaet, aldri et krav', 'hypothesisSuggestedTestDays', TAK.testperiode, (n) => sett('hypothesisSuggestedTestDays', n))}
        </Rad>
        <div className="panel-innst-om">
          <Om tittel="fristene" testid="om-fristene" sammendrag={`høyst ${TAK.frist} dager; en frist kan kortes, aldri forlenges`}>
            <p>En frist er høyst {TAK.frist} dager. Den kan kortes for én hypotese i detaljen, aldri forlenges.</p>
            <p>En endring her gjelder hypoteser som tas til vurdering eller avsluttes etter at du har lagret — fristene som løper, står.</p>
          </Om>
        </div>
      </Seksjon>

      <Seksjon id="skjemaet" tittel="Skjemaet">
        <Rad
          id="minste"
          navn="Minste antall tegn"
          kode="hypothesisMinFieldLength"
          handling="Endre"
          endret={endret('minste')}
          verdi={
            <>
              <strong>{verdi('hypothesisMinFieldLength')}</strong> per ledd
            </>
          }
        >
          {tall('minste', 'Tegn per ledd før «Send inn» virker', 'hypothesisMinFieldLength', TAK.minste, (n) => sett('hypothesisMinFieldLength', n))}
        </Rad>
        {/* H3.3 (W9, avvik 9): katalogen er en liste med ukjent antall — en egen side, nådd fra én rad. */}
        <Rad
          id="kilder"
          navn="Kilder"
          kode="MetricSource"
          handling="Endre"
          til="/h/innstillinger/kilder"
          verdi={
            kilder.data ? (
              <>
                <strong>{kilder.data.items.length === 1 ? '1 kilde' : `${kilder.data.items.length} kilder`}</strong>
                {kilder.data.openQuestionCount ? ` · ${kilder.data.openQuestionCount} ${kilder.data.openQuestionCount === 1 ? 'spørsmål venter' : 'spørsmål venter'}` : ''}
              </>
            ) : kilder.isError ? (
              'Kunne ikke hentes'
            ) : (
              'Henter …'
            )
          }
        />
        <Rad
          id="hjelpetekst"
          navn="Hjelpetekst"
          kode="hypothesisHelpText"
          handling="Endre"
          endret={endret('hjelpetekst')}
          verdi={
            hjelp ? (
              <>
                <strong className="h-innst-sitat">«{hjelp}»</strong> · vises i skjemaet
              </>
            ) : (
              'Ingen'
            )
          }
          verdiSmal={hjelp ? 'Vises i skjemaet' : 'Ingen'}
        >
          <label className="panel-felt panel-innst-bred">
            <span>
              Hjelpetekst i skjemaet ({hjelp.length}/{MAKS.hjelp})
            </span>
            <textarea value={hjelp} rows={3} maxLength={MAKS.hjelp} onChange={(e) => sett('hypothesisHelpText', e.target.value)} data-testid="h-hjelpetekst" />
          </label>
        </Rad>
      </Seksjon>

      <Seksjon id="ordlyd" tittel="Ordlyd og koder">
        <Rad
          id="statusnavn"
          navn="Statusnavn og løfter"
          kode="hypothesisStatusWording"
          handling="Endre"
          til="/h/innstillinger/statusnavn"
          endret={endret('statusnavn')}
          verdi={
            <>
              <strong>{statuser.length} statuser</strong>
              {mottatt ? <> · «{mottatt.promise}»</> : null}
            </>
          }
          verdiSmal={`${statuser.length} statuser`}
        />
        <Rad
          id="avslagskoder"
          navn="Avslagskoder"
          kode="hypothesisRejectReasonWording"
          handling="Endre"
          til="/h/innstillinger/avslagskoder"
          endret={endret('avslagskoder')}
          verdi={
            <>
              <strong>{avslag.length}</strong> · {koder(avslag)}
            </>
          }
          verdiSmal={`${avslag.length} koder`}
        />
        <Rad
          id="stoppkoder"
          navn="Stoppkoder"
          kode="hypothesisStopReasonWording"
          handling="Endre"
          til="/h/innstillinger/stoppkoder"
          endret={endret('stoppkoder')}
          verdi={
            <>
              <strong>{stopp.length}</strong> · {koder(stopp)}
            </>
          }
          verdiSmal={`${stopp.length} koder`}
        />
        <Rad
          id="svarmaler"
          navn="Svarmaler"
          kode="hypothesisResponseTemplates"
          handling="Endre"
          til="/h/innstillinger/svarmaler"
          endret={endret('svarmaler')}
          verdi={
            maler.length ? (
              <>
                <strong>{maler.length}</strong> · {bruk.join(' · ')}
              </>
            ) : (
              'Ingen — svarfeltet starter tomt'
            )
          }
          verdiSmal={`${maler.length} maler`}
        />
      </Seksjon>

      {/* H3.2 (P3, svar 4): én rad i R5.3-formen. Av som standard; av betyr at kontrollene i kjøringen,
          tallinjen i loggen og sorteringen i registeret ikke finnes. Å skru den av sletter ingenting. */}
      <Seksjon id="moeter" tittel="Møter">
        <Rad
          id="tall"
          navn="Menneskesatte tall"
          kode="hypothesisScoresEnabled"
          handling={null}
          endret={endret('tall')}
          verdi={
            <span className="h-innst-bryter">
              <Bryter label="Menneskesatte tall" skjultEtikett paa={verdi('hypothesisScoresEnabled')} onChange={(v) => sett('hypothesisScoresEnabled', v)} testid="tall-bryter" />
              <span className="hb-dempet">{verdi('hypothesisScoresEnabled') ? 'Impact, Confidence og Effort settes i møtet — aldri en sum' : 'Av — ingen tall settes i møtet'}</span>
            </span>
          }
        />
        <div className="panel-innst-om">
          <Om tittel="tallene" testid="om-tallene" sammendrag="et menneske setter dem i møtet; leser og opp og beslutningstakeren ser dem">
            <p>Impact, Confidence og Effort settes av den som registrerer i møtet, og kan endres av beslutningstakeren før han bekrefter. Reach settes aldri av et menneske — det er grunnlagets tall.</p>
            <p>Tallene vises for leser og opp og for møtets beslutningstaker, aldri for innsenderen og aldri på skjermen i rommet. Registeret kan sorteres på én av dem om gangen.</p>
          </Om>
        </div>
      </Seksjon>

      <Seksjon id="oppbevaring" tittel="Oppbevaring">
        <Rad
          id="oppbevaring"
          navn="Avsluttede hypoteser"
          kode="hypothesisRetentionYears"
          handling="Endre"
          endret={endret('oppbevaring')}
          verdi={
            <>
              <strong>{verdi('hypothesisRetentionYears')} år</strong> etter avslutning
            </>
          }
          verdiSmal={`${verdi('hypothesisRetentionYears')} år`}
        >
          {tall('oppbevaring', 'År etter avslutningen', 'hypothesisRetentionYears', TAK.aar, (n) => sett('hypothesisRetentionYears', n))}
        </Rad>
      </Seksjon>

      {/* H3.5 (P7, skjerm 18–19): én rad som sier hva som sendes og peker dit valget gjøres. Ingen
          avkrysning her — to steder å velge det samme ville gitt to sannheter. */}
      <Seksjon id="utgaaende" tittel="Utgående">
        <Rad id="hendelser" navn="Varsler til deres system" kode="webhooks" handling="Velg i Organisasjon" til="/organisasjon" verdi={hypotesemottakere(mottakere)} />
      </Seksjon>
    </>
  );

  const kodeRader = (art: 'avslag' | 'stopp') => {
    const liste = art === 'avslag' ? avslag : stopp;
    const skriv = (i: number, endring: Partial<(typeof liste)[number]>) => {
      const ny = liste.map((w, j) => (j === i ? { ...w, ...endring } : w));
      if (art === 'avslag') sett('hypothesisRejectReasonWording', ny as typeof avslag);
      else sett('hypothesisStopReasonWording', ny as typeof stopp);
    };
    const original = art === 'avslag' ? fra.hypothesisRejectReasonWording : fra.hypothesisStopReasonWording;
    return liste.map((w, i) => (
      <Rad
        key={w.code}
        id={`${art}-${w.code}`}
        navn={w.label || '—'}
        kode={w.code}
        handling="Endre"
        endret={JSON.stringify(w) !== JSON.stringify(original.find((o) => o.code === w.code))}
        verdi={
          <>
            {w.template ?? 'Ingen mal'}
            {w.hidden ? <span className="h-innst-skjult"> · skjult i panelet</span> : null}
          </>
        }
        verdiSmal={w.hidden ? 'Skjult' : undefined}
      >
        <label className="panel-felt">
          <span>Etikett</span>
          <input type="text" value={w.label} maxLength={60} onChange={(e) => skriv(i, { label: e.target.value })} data-testid={`${art}-etikett-${w.code}`} />
        </label>
        <label className="panel-felt panel-innst-bred">
          <span>
            Malsetning ({(w.template ?? '').length}/{MAKS.mal})
          </span>
          <textarea value={w.template ?? ''} rows={3} maxLength={MAKS.mal} onChange={(e) => skriv(i, { template: e.target.value })} data-testid={`${art}-mal-${w.code}`} />
        </label>
        <Bryter label="Skjul koden i panelet" paa={w.hidden} onChange={(v) => skriv(i, { hidden: v })} testid={`${art}-skjul-${w.code}`} />
      </Rad>
    ));
  };

  const kandidater = (config.data?.members ?? []).filter((p) => !deciders.includes(p.id));
  const undersideInnhold: Record<Underside, () => ReactNode> = {
    statusnavn: () =>
      statuser.map((w, i) => (
        <Rad
          key={w.key}
          id={`status-${w.key}`}
          navn={w.label || '—'}
          kode={w.key}
          handling="Endre"
          endret={JSON.stringify(w) !== JSON.stringify(fra.hypothesisStatusWording.find((o) => o.key === w.key))}
          verdi={
            <span className="h-innst-status">
              <Ikon navn={hypoteseOppforing(w.key).ikon} />
              {w.promise}
            </span>
          }
          verdiSmal={w.promise}
        >
          <label className="panel-felt">
            <span>Navn på pillen</span>
            <input type="text" value={w.label} maxLength={40} onChange={(e) => sett('hypothesisStatusWording', statuser.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} data-testid={`status-etikett-${w.key}`} />
          </label>
          <label className="panel-felt panel-innst-bred">
            <span>
              Løftet ({w.promise.length}/{MAKS.lofte})
            </span>
            <textarea value={w.promise} rows={2} maxLength={MAKS.lofte} onChange={(e) => sett('hypothesisStatusWording', statuser.map((x, j) => (j === i ? { ...x, promise: e.target.value } : x)))} data-testid={`status-lofte-${w.key}`} />
          </label>
        </Rad>
      )),
    avslagskoder: () => kodeRader('avslag'),
    stoppkoder: () => kodeRader('stopp'),
    svarmaler: () => (
      <>
        <ul className="panel-innst-liste" aria-label="Svarmaler">
          {maler.map((m, i) => (
            <li key={i} className="panel-innst-mal" data-testid={`hmal-${i}`}>
              <label className="panel-felt">
                <span>Navn</span>
                <input type="text" value={m.name} maxLength={60} onChange={(e) => sett('hypothesisResponseTemplates', maler.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} data-testid={`hmal-navn-${i}`} />
              </label>
              <label className="panel-felt">
                <span>Avgjørelse</span>
                <select value={m.use} onChange={(e) => sett('hypothesisResponseTemplates', maler.map((x, j) => (j === i ? { ...x, use: e.target.value as Bruk } : x)))} data-testid={`hmal-bruk-${i}`}>
                  {BRUK.map(([k, n]) => (
                    <option key={k} value={k}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <label className="panel-felt panel-innst-bred">
                <span>
                  Tekst ({m.text.length}/{MAKS.mal})
                </span>
                <textarea value={m.text} rows={2} maxLength={MAKS.mal} onChange={(e) => sett('hypothesisResponseTemplates', maler.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} data-testid={`hmal-tekst-${i}`} />
              </label>
              <Knapp label="Fjern" onClick={() => sett('hypothesisResponseTemplates', maler.filter((_, j) => j !== i))} testid={`hmal-fjern-${i}`} />
            </li>
          ))}
        </ul>
        <Knapp label="Ny mal" grunn={maler.length >= 20 ? 'Høyst 20 maler' : null} onClick={() => sett('hypothesisResponseTemplates', [...maler, { name: '', text: '', use: 'trenger_mer' }])} testid="hmal-ny" />
      </>
    ),
    beslutningstakere: () => <Beslutningstakere ider={deciders} navnPaa={navnPaa} kandidater={kandidater} onEndre={(ider) => sett('hypothesisDeciders', ider)} />,
  };

  return (
    <>
      <header className="panel-topp">
        <div className="h-innst-tittel">
          {underside ? (
            <Link to="/h/innstillinger" className="panel-knapp-lenke h-innst-tilbake">
              <Ikon navn="chevron-back-outline" />
              Innstillinger
            </Link>
          ) : null}
          <h1 className="panel-h1">{tittel}</h1>
        </div>
        {/* «Vis tekniske navn» står til høyre på tittellinjen, som konseptets P9. */}
        <div className="h-innst-topp-hoyre">
          <TekniskeBryter paa={side.tekniske} onChange={side.setTekniske} />
          <Kartknapp tittel="Innstillinger · Hypoteser" taster={TASTER} />
        </div>
      </header>

      <div className="panel-innst">
        <div className="panel-innst-hode">
          <p className="panel-innst-lead">{underside ? UNDERSIDE_TEKST[underside] : 'Rommet Hypoteser — hvem som avgjør, hvor lenge de får, og hva dere lover den som sender inn.'}</p>
        </div>
        <RadProvider value={side}>{underside ? <div className="h-innst-underside">{undersideInnhold[underside]()}</div> : hovedside}</RadProvider>
      </div>

      <Fot antall={antall} endretNavn={endretNavn} lagrer={oppdater.isPending} feil={feil} sperret={sperret} onLagre={lagre} onForkast={tøm} onLukkFeil={() => setFeil(null)} />
      {toast ? <Toast innhold={toast} onLukk={() => setToast(null)} /> : null}
    </>
  );
}

/**
 * Beslutningstakerne (`admin-hypoteser.jsx`, Underside · beslutningstakere): én rad per person med
 * «Ta av lista», og «Legg til» blant rommets aktive personer. Tjeneren krever minst bidragsyter i
 * rommet eller eier (422 `validation_failed`) — kandidatene er `config.members`, som er nettopp dem.
 */
function Beslutningstakere({ ider, navnPaa, kandidater, onEndre }: { ider: string[]; navnPaa: (id: string) => string; kandidater: ReadonlyArray<{ id: string; displayName?: string }>; onEndre: (ider: string[]) => void }) {
  const [valgt, setValgt] = useState('');
  return (
    <>
      <ul className="h-innst-folk" aria-label="Beslutningstakere">
        {ider.map((id) => (
          <li key={id} className="h-innst-person">
            <strong>{navnPaa(id)}</strong>
            <button type="button" className="panel-knapp-lenke" aria-label={`Ta ${navnPaa(id)} av lista`} onClick={() => onEndre(ider.filter((x) => x !== id))}>
              Ta av lista
            </button>
          </li>
        ))}
      </ul>
      {ider.length === 0 ? <p className="hb-dempet">Ingen står på lista. Ingen hypotese kan velges før minst én gjør det.</p> : null}
      <div className="h-innst-legg-til">
        <label className="panel-felt">
          <span>Legg til</span>
          <select value={valgt} onChange={(e) => setValgt(e.target.value)} data-testid="besl-ny">
            <option value="">Velg en person</option>
            {kandidater.map((p) => (
              <option key={p.id} value={p.id}>
                {p.displayName ?? 'en kollega uten visningsnavn'}
              </option>
            ))}
          </select>
        </label>
        <Knapp
          label="Legg til"
          grunn={valgt ? (ider.length >= 20 ? 'Høyst 20 på lista' : null) : 'Velg en person først'}
          onClick={() => {
            onEndre([...ider, valgt]);
            setValgt('');
          }}
          testid="besl-legg-til"
        />
      </div>
    </>
  );
}

/** Hypotesehendelsene i kontrakten (gruppen «Hypoteser» i Organisasjon, med notisen fra H3.1). */
const HYPOTESEHENDELSER = HENDELSER.filter((h) => h.gruppe === 'hypoteser').map((h) => h.navn);

/**
 * «1 mottaker får 6 av 10 hypotesehendelser», eller «Ingen mottaker får hypotesehendelser» (skjerm 19).
 * Panelet virker uansett: «Til deg» og fristene står i rommet.
 */
function hypotesemottakere(q: ReturnType<typeof useWebhooks>): ReactNode {
  if (q.isPending) return 'Henter …';
  if (q.isError) return 'Kunne ikke hentes';
  const med = (q.data ?? []).map((w) => w.events.filter((e) => HYPOTESEHENDELSER.includes(e)).length).filter((n) => n > 0);
  if (med.length === 0) return 'Ingen mottaker får hypotesehendelser';
  const hvor = med.length === 1 ? `${med[0]} av ${HYPOTESEHENDELSER.length}` : med.every((n) => n === med[0]) ? `${med[0]} av ${HYPOTESEHENDELSER.length}` : 'ulike';
  return (
    <>
      <strong>{med.length === 1 ? '1 mottaker' : `${med.length} mottakere`}</strong> får {hvor} hypotesehendelser
    </>
  );
}
